import {AxiosInstance, InternalAxiosRequestConfig, AxiosHeaders} from 'axios';
import {GetTokenSilentlyOptions} from '@auth0/auth0-react';
import {sendSentryMessage} from 'utils/error-handling';

type TokenGenerator = (options?: GetTokenSilentlyOptions) => Promise<string>;

/**
 * This is a singleton that is used to intercept axios requests and attach
 * Auth0 access token to the Authorization header
 * @see {@link https://auth0.github.io/auth0-react/interfaces/auth0_context.auth0contextinterface.html#getaccesstokensilently | Auth0 getAccessTokenSilently()}
 * @see {@link https://spothero.github.io/fe-utils/module-APIUtils.html#.attachInterceptor | FE Utils attachInterceptor()}
 */
export class AuthServiceInterceptor {
    private getToken: TokenGenerator;

    constructor() {
        // Initialize with a default token generator that resolves to empty string
        this.getToken = () => Promise.resolve('');
    }

    /**
     * Sets the token generator function that will later be called in the interceptor
     * @param tokenGenerator - an async function that gets an access token (getAccessTokenSilently())
     */
    setTokenGenerator(
        tokenGenerator: TokenGenerator
    ): Promise<AuthServiceInterceptor> {
        return new Promise((resolve) => {
            this.getToken = tokenGenerator;
            resolve(this);
        });
    }

    /**
     * Adds an interceptor to an axios instance that gets a token using the provided options,
     * then attaches the token information in the Authorization header.
     *
     * Different APIs (e.g. V1/Monolith and V2/Craig) will have different audience/scope.
     * Within the category of V1 APIs, multiple axios instances have been created to handle
     * specific needs.
     *
     * If no tokenOptions are specified, the default options (see auth0 initialization) will be used.
     *
     * @param axiosInstance - a given axios instance to attach the interceptor to
     * @param tokenOptions - See GetTokenSilentlyOptions from Auth0 for usage
     */
    attachInterceptorWithToken(
        axiosInstance: AxiosInstance,
        tokenOptions?: GetTokenSilentlyOptions
    ): void {
        axiosInstance.interceptors.request.use(
            async (config: InternalAxiosRequestConfig) => {
                let interceptedConfig: InternalAxiosRequestConfig | undefined;

                try {
                    const token = await this.getToken(tokenOptions);

                    // ensures config is always an AxiosHeaders instance
                    const headers =
                        config.headers instanceof AxiosHeaders
                            ? config.headers
                            : new AxiosHeaders(config.headers);

                    headers.set('Authorization', `Bearer ${token}`);

                    interceptedConfig = {
                        ...config,
                        headers,
                    };

                    return interceptedConfig;
                } catch (error: unknown) {
                    if (
                        error &&
                        typeof error === 'object' &&
                        'message' in error &&
                        typeof error.message === 'string' &&
                        error.message.includes('cancelToken')
                    ) {
                        sendSentryMessage({
                            error,
                            additionalData: {
                                config,
                                interceptedConfig: interceptedConfig || {},
                            },
                        });
                    }

                    return Promise.reject(error);
                }
            }
        );
    }
}

const instance = new AuthServiceInterceptor();

export default instance;
